/**
 * Backup Provider
 *
 * Holds the chosen backup format (set when the user confirms the
 * backup_format_selector_ui card) so the Control Panel's BackupSection can
 * show the "Download backup" UI. Also exposes a small helper that decodes
 * a base64 payload into a browser file download.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAppSelector } from '@/stores/store';
import { selectCurrentThreadId } from '@/stores/selectors/chat-selectors';

export type BackupFormat = string; // Free-form to match agent's "json"|"csv"|"terraform"|future

export interface BackupContextValue {
  backupFormat: BackupFormat | null;
  setBackupFormat: (format: BackupFormat | null) => void;
  triggerBackupDownload: (filename: string, contentBase64: string) => void;
  isBackupDownloaded: boolean;
  setIsBackupDownloaded: (downloaded: boolean) => void;
  /** True once any side (chat card or control panel) has submitted the proceed message. */
  hasProceedBeenSubmitted: boolean;
  markProceedSubmitted: () => void;
}

const BackupContext = createContext<BackupContextValue | null>(null);

interface BackupProviderProps {
  children: ReactNode;
}

export function BackupProvider({ children }: BackupProviderProps) {
  const [backupFormat, setBackupFormat] = useState<BackupFormat | null>(null);
  const [isBackupDownloaded, setIsBackupDownloaded] = useState(false);
  const [hasProceedBeenSubmitted, setHasProceedBeenSubmitted] = useState(false);
  const markProceedSubmitted = useCallback(() => setHasProceedBeenSubmitted(true), []);

  // Reset all backup state when the thread changes so a new conversation always
  // starts fresh — prevents the format/download/proceed flags from a previous
  // thread blocking the BackupFormatSelectorComponent auto-submit.
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const prevThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentThreadId !== prevThreadIdRef.current) {
      prevThreadIdRef.current = currentThreadId;
      if (currentThreadId) {
        setBackupFormat(null);
        setIsBackupDownloaded(false);
        setHasProceedBeenSubmitted(false);
      }
    }
  }, [currentThreadId]);

  const triggerBackupDownload = useCallback(
    (filename: string, contentBase64: string) => {
      if (!filename || !contentBase64) return;
      try {
        const a = document.createElement('a');
        a.href = `data:application/octet-stream;base64,${contentBase64}`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        // Non-critical — keep silent so it doesn't break the chat UI.
      }
    },
    []
  );

  const value: BackupContextValue = {
    backupFormat,
    setBackupFormat,
    triggerBackupDownload,
    isBackupDownloaded,
    setIsBackupDownloaded,
    hasProceedBeenSubmitted,
    markProceedSubmitted,
  };

  return <BackupContext.Provider value={value}>{children}</BackupContext.Provider>;
}

export function useBackupContext(): BackupContextValue {
  const ctx = useContext(BackupContext);
  if (!ctx) {
    throw new Error('useBackupContext must be used within a BackupProvider');
  }
  return ctx;
}

export function useOptionalBackupContext(): BackupContextValue | null {
  return useContext(BackupContext);
}
